import {
  getTimeString,
  isDaysUnit,
  isHoursUnit,
  isMillisecondsUnit,
  isMinutesUnit,
  isMonthUnit,
  isSecondsUnit,
  isYearsUnit,
  TimeMode,
  TimeUnit,
  useTranslation,
} from "@opendash/core";
import { CustomParseFieldProps } from "@opendash/plugin-parse";
import { InputNumber, Select } from "antd";

import dayjs from "dayjs";
import React, { useEffect } from "react";

const duration = require("dayjs/plugin/duration");
dayjs.extend(duration);

type DurationSettingsComponentsProps = React.PropsWithChildren & {
  value: number;
  onChange?: (value: number) => void;
  unit: TimeUnit;
};
const DurationSettingsComponents = ({
  value = 0,
  onChange,
  unit,
}: DurationSettingsComponentsProps) => {
  const t = useTranslation();
  const { unit: dayJSUnit, factor } = getDayJSUnit(unit);

  const [editInterval, setEditInterval] = React.useState<{
    amount: number;
    unit: dayjs.UnitType;
  } | null>({ amount: value, unit: dayJSUnit });
  React.useEffect(() => {
    if (value !== 0 && (!editInterval.amount || editInterval.amount === 0)) {
      //@ts-expect-error
      const adjustedValue = dayjs.duration(value, unit).as(editInterval.unit);
      setEditInterval({ amount: adjustedValue, unit: editInterval.unit });
    }
  }, [value]);
  useEffect(() => {
    const dur = dayjs
      //@ts-expect-error
      .duration(editInterval.amount, editInterval.unit)
      .as(unit);
    if (onChange) onChange(dur);
  }, [editInterval]);

  const selectAfter = (
    <Select
      value={editInterval.unit}
      onChange={(unit) => {
        setEditInterval((prev) => {
          return {
            unit,
            //@ts-expect-error
            amount: dayjs.duration(prev.amount, prev.unit).as(unit),
          };
        });
      }}
    >
      <Select.Option
        value={"millisecond"}
        label={t("opendash:ui.milliseconds")}
      >
        {t(
          "opendash:ui.millisecond" + (editInterval.amount > 1 ? "_plural" : "")
        )}
      </Select.Option>
      <Select.Option value={"second"} label={t("opendash:ui.second")}>
        {t("opendash:ui.second" + (editInterval.amount > 1 ? "_plural" : ""))}
      </Select.Option>
      <Select.Option value={"minute"} label={t("opendash:ui.minute")}>
        {t("opendash:ui.minute" + (editInterval.amount > 1 ? "_plural" : ""))}
      </Select.Option>
      <Select.Option value={"hour"} label={t("opendash:ui.hour")}>
        {t("opendash:ui.hour" + (editInterval.amount > 1 ? "_plural" : ""))}
      </Select.Option>
      <Select.Option value={"day"} label={t("opendash:ui.day")}>
        {t("opendash:ui.day" + (editInterval.amount > 1 ? "_plural" : ""))}
      </Select.Option>
      <Select.Option value={"year"} label={t("opendash:ui.year")}>
        {t("opendash:ui.year" + (editInterval.amount > 1 ? "_plural" : ""))}
      </Select.Option>
    </Select>
  );
  return (
    <>
      {!onChange && (
        <span>{getTimeString(TimeMode.DurationMode, "full", unit, value)}</span>
      )}
      {onChange && (
        <div style={{ width: "100%" }}>
          <InputNumber
            style={{ width: "100%" }}
            decimalSeparator=","
            value={editInterval.amount}
            onChange={(newVal) => {
              setEditInterval((prev) => {
                return { ...prev, amount: newVal };
              });
            }}
            addonAfter={selectAfter}
          />
        </div>
      )}
    </>
  );
};

const getDayJSUnit = (
  unit: string
): { factor: number; unit: dayjs.UnitType } => {
  if (isMillisecondsUnit(unit)) {
    return { factor: 1, unit: "millisecond" };
  }
  if (isSecondsUnit(unit)) {
    return { factor: 1, unit: "second" };
  }
  if (isMinutesUnit(unit)) {
    return { factor: 1, unit: "minute" };
  }
  if (isHoursUnit(unit)) {
    return { factor: 1, unit: "hour" };
  }
  if (isDaysUnit(unit)) {
    return { factor: 1, unit: "day" };
  }
  if (isMonthUnit(unit)) {
    return { factor: 1, unit: "month" };
  }
  if (isYearsUnit(unit)) {
    return { factor: 1, unit: "year" };
  }
};
const DurationParseComponent = ({
  value,
  setValue,
  parseObject,
  fieldname,
  props,
}: CustomParseFieldProps<DurationSettingsComponentsProps>) => {
  return (
    <DurationSettingsComponents
      value={parseObject.get(fieldname)}
      onChange={setValue}
      unit={props.unit}
    />
  );
};
export { DurationParseComponent, DurationSettingsComponents };
export type { DurationSettingsComponentsProps };
